import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';

const ORANGE = '#FF8A4C';

const PRINT_SIDE_FILTERS = ['SP', 'RP', 'CBW SP'];
const STATUSES = [
  { value: 'active',       label: 'Active' },
  { value: 'non_standard', label: 'Non-standard' },
  { value: 'do_not_use',   label: 'Do not use' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.value, s.label]));

// SP / RP / CBW SP label from raw print_side + substrate
const sideLabel = (printSide, substrate) => {
  if (printSide === 'SP' && substrate && /CBW/i.test(substrate)) return 'CBW SP';
  return printSide || '—';
};

const statusPillClass = (status) => ({
  active:        'bg-status-ok/12 text-status-ok border-status-ok/35',
  non_standard:  'bg-ink-yellow/12 text-ink-yellow border-ink-yellow/35',
  do_not_use:    'bg-ink-magenta/12 text-ink-magenta border-ink-magenta/35',
}[status] ?? 'bg-bg-card text-text-tertiary border-border-strong');

const sidePillClass = (label) => {
  if (label === 'RP')    return 'bg-status-purple/12 text-status-purple border-status-purple/35';
  if (label === 'CBW SP')return 'bg-ink-yellow/12 text-ink-yellow border-ink-yellow/35';
  if (label === 'SP')    return 'bg-ink-yellow/12 text-ink-yellow border-ink-yellow/35';
  return 'bg-bg-card text-text-tertiary border-border-strong';
};

const fmt = (n) => (n == null ? '—' : Number(n).toFixed(2));

const Pill = ({ children, class: cls = '' }) => (
  <span class={`inline-flex items-center rounded-full border px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.04em] ${cls}`}>
    {children}
  </span>
);

const DensityRow = ({ label, d, muted = false }) => {
  if (d.c == null && d.m == null && d.y == null && d.k == null) return null;
  const tone = muted ? 'text-text-tertiary' : 'text-text-secondary';
  return (
    <div class={`font-mono text-[12px] ${tone}`}>
      {label && <span class="mr-1 text-text-muted">{label}</span>}
      <span class="text-ink-cyan font-medium">C</span> {fmt(d.c)} &nbsp;
      <span class="text-ink-magenta font-medium">M</span> {fmt(d.m)} &nbsp;
      <span class="text-ink-yellow font-medium">Y</span> {fmt(d.y)} &nbsp;
      <span class={`${muted ? 'text-text-secondary' : 'text-text-primary'} font-medium`}>K</span> {fmt(d.k)}
    </div>
  );
};

const ProfileCard = ({ profile, onOpen }) => {
  const side = sideLabel(profile.printSide, profile.substrate);
  const dimmed = profile.status === 'do_not_use';
  return (
    <button
      type="button"
      onClick={() => onOpen(profile)}
      class={`relative block w-full overflow-hidden rounded-md border border-border-subtle bg-bg-card p-3 text-left transition hover:border-border-strong hover:bg-bg-hover ${dimmed ? 'opacity-70' : ''}`}
    >
      <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: ORANGE }} aria-hidden="true" />
      <div class="mb-1.5 flex flex-wrap items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="m-0 font-serif text-[15px] font-medium leading-tight text-text-primary">
            {profile.customerName}
          </p>
          <p class="m-0 mt-0.5 text-[12px] text-text-secondary">
            {[profile.pressType, profile.substrate, profile.version].filter(Boolean).join(' · ') || profile.profileName}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-1.5">
          {profile.pressType && <Pill class="border-border-strong bg-bg-base text-text-tertiary">{profile.pressType}</Pill>}
          {side !== '—' && <Pill class={sidePillClass(side)}>{side}</Pill>}
          <Pill class={statusPillClass(profile.status)}>{STATUS_LABEL[profile.status]}</Pill>
        </div>
      </div>

      <div class="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-tertiary">
        {profile.plateCurve && <span>📄 {profile.plateCurve}</span>}
        {profile.media && <span>📦 {profile.media}</span>}
        {profile.lpi != null && <span>LPI <span class="font-mono text-text-secondary">{profile.lpi}</span></span>}
        {profile.dotShape && <span>Dot <span class="font-mono text-text-secondary">{profile.dotShape}</span></span>}
      </div>

      <div class="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
        <DensityRow d={profile.density} />
        <DensityRow d={profile.densityWt} label="WT:" muted />
        <div class="ml-auto text-[10px] text-text-muted">
          {profile.dateUpdated ? `Updated ${profile.dateUpdated}` : profile.dateApproved ? `Approved ${profile.dateApproved}` : ''}
        </div>
      </div>
    </button>
  );
};

const DetailModal = ({ profile, onClose, onSaved, customers, isAdmin }) => {
  const editing = profile?.id != null;
  const [mode, setMode] = useState('view'); // view | edit
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!profile) return;
    setMode(editing ? 'view' : 'edit');
    setForm({
      customerId: profile.customerId ?? customers?.[0]?.id ?? null,
      profileName: profile.profileName ?? '',
      pressType: profile.pressType ?? '',
      substrate: profile.substrate ?? '',
      printSide: profile.printSide ?? '',
      version: profile.version ?? '',
      status: profile.status ?? 'active',
      tags: profile.tags ?? [],
      dateApproved: profile.dateApproved ?? '',
      dateUpdated: profile.dateUpdated ?? '',
      lpi: profile.lpi ?? '',
      dotShape: profile.dotShape ?? '',
      plateCurve: profile.plateCurve ?? '',
      media: profile.media ?? '',
      densityC: profile.density?.c ?? '',
      densityM: profile.density?.m ?? '',
      densityY: profile.density?.y ?? '',
      densityK: profile.density?.k ?? '',
      densityWtC: profile.densityWt?.c ?? '',
      densityWtM: profile.densityWt?.m ?? '',
      densityWtY: profile.densityWt?.y ?? '',
      densityWtK: profile.densityWt?.k ?? '',
      notes: profile.notes ?? '',
    });
    setError(null);
  }, [profile]);

  if (!profile) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.currentTarget.value }));
  const trim = (v) => { const s = String(v ?? '').trim(); return s === '' ? null : s; };
  const num = (v) => { const s = String(v ?? '').trim(); return s === '' ? null : Number(s); };

  const save = async () => {
    if (!form.profileName?.trim() || !form.customerId) return;
    setBusy(true); setError(null);
    try {
      const payload = {
        customerId: Number(form.customerId),
        profileName: form.profileName.trim(),
        pressType: trim(form.pressType),
        substrate: trim(form.substrate),
        printSide: form.printSide || null,
        version: trim(form.version),
        status: form.status,
        tags: form.tags ?? [],
        dateApproved: trim(form.dateApproved),
        dateUpdated: trim(form.dateUpdated),
        lpi: form.lpi === '' ? null : Number(form.lpi),
        dotShape: trim(form.dotShape),
        plateCurve: trim(form.plateCurve),
        media: trim(form.media),
        densityC: num(form.densityC), densityM: num(form.densityM),
        densityY: num(form.densityY), densityK: num(form.densityK),
        densityWtC: num(form.densityWtC), densityWtM: num(form.densityWtM),
        densityWtY: num(form.densityWtY), densityWtK: num(form.densityWtK),
        notes: trim(form.notes),
      };
      const saved = editing
        ? await api.patch(`/customer-profiles/${profile.id}`, payload)
        : await api.post('/customer-profiles', payload);
      onSaved?.(saved);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete profile "${profile.profileName}"?`)) return;
    try { await api.delete(`/customer-profiles/${profile.id}`); onSaved?.(); }
    catch (err) { alert(err.message ?? 'Failed'); }
  };

  const side = sideLabel(profile.printSide, profile.substrate);

  return (
    <div class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="w-full max-w-2xl overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="flex items-start justify-between gap-3 border-b border-border-subtle bg-bg-header px-5 py-4">
          <div>
            <p class="eyebrow mb-1" style={{ color: ORANGE }}>{editing ? 'Profile' : 'New profile'}</p>
            <p class="m-0 font-serif text-[18px] font-medium text-text-primary">{profile.customerName ?? '—'}</p>
            <p class="m-0 mt-0.5 text-[12px] text-text-secondary">{profile.profileName}</p>
          </div>
          <div class="flex flex-wrap items-center gap-1.5">
            {profile.pressType && <Pill class="border-border-strong bg-bg-base text-text-tertiary">{profile.pressType}</Pill>}
            {side !== '—' && <Pill class={sidePillClass(side)}>{side}</Pill>}
            <Pill class={statusPillClass(profile.status)}>{STATUS_LABEL[profile.status]}</Pill>
          </div>
        </div>

        {mode === 'view' ? (
          <div class="space-y-4 p-5">
            <div class="grid grid-cols-2 gap-3 text-[13px]">
              <Field label="Plate curve" value={profile.plateCurve} mono />
              <Field label="Media" value={profile.media} />
              <Field label="LPI" value={profile.lpi} mono />
              <Field label="Dot shape" value={profile.dotShape} mono />
              <Field label="Version" value={profile.version} />
              <Field label="Source" value={profile.source} mono muted />
              <Field label="Date approved" value={profile.dateApproved} mono />
              <Field label="Date updated" value={profile.dateUpdated} mono />
            </div>

            <div>
              <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Densities</p>
              <div class="grid grid-cols-2 gap-3 rounded-md border border-border-subtle bg-bg-card p-3">
                <div>
                  <p class="mb-1 text-[11px] text-text-muted">Standard</p>
                  <DensityRow d={profile.density} />
                </div>
                <div>
                  <p class="mb-1 text-[11px] text-text-muted">With white</p>
                  <DensityRow d={profile.densityWt} muted />
                </div>
              </div>
            </div>

            {profile.notes && (
              <div>
                <p class="mb-1 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Notes</p>
                <p class="m-0 whitespace-pre-wrap text-[13px] text-text-body">{profile.notes}</p>
              </div>
            )}
            {profile.tags?.length > 0 && (
              <div class="flex flex-wrap gap-1">
                {profile.tags.map((t) => <Pill key={t} class="border-border-strong bg-bg-base text-text-tertiary">{t}</Pill>)}
              </div>
            )}

            <ReadingsSection profileId={profile.id} customerId={profile.customerId} isAdmin={isAdmin} onClose={onClose} />
          </div>
        ) : (
          <div class="space-y-3 p-5">
            <div class="grid grid-cols-2 gap-3">
              <Input label="Customer">
                <select value={form.customerId ?? ''} onChange={set('customerId')} class={inputClass}>
                  {customers?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Input>
              <Input label="Profile name"><input type="text" value={form.profileName} onInput={set('profileName')} class={inputClass} /></Input>
              <Input label="Press type"><input type="text" value={form.pressType} onInput={set('pressType')} class={inputClass} /></Input>
              <Input label="Substrate"><input type="text" value={form.substrate} onInput={set('substrate')} class={inputClass} /></Input>
              <Input label="Print side">
                <select value={form.printSide} onChange={set('printSide')} class={inputClass}>
                  <option value="">—</option>
                  <option value="SP">SP</option>
                  <option value="RP">RP</option>
                </select>
              </Input>
              <Input label="Status">
                <select value={form.status} onChange={set('status')} class={inputClass}>
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Input>
              <Input label="LPI"><input type="number" value={form.lpi} onInput={set('lpi')} class={inputClass} /></Input>
              <Input label="Dot shape"><input type="text" value={form.dotShape} onInput={set('dotShape')} class={inputClass} /></Input>
              <Input label="Version"><input type="text" value={form.version} onInput={set('version')} class={inputClass} /></Input>
              <Input label="Date approved (YYYY-MM-DD)"><input type="text" value={form.dateApproved} onInput={set('dateApproved')} class={inputClass} /></Input>
              <Input label="Plate curve" wide><input type="text" value={form.plateCurve} onInput={set('plateCurve')} class={inputClass} /></Input>
              <Input label="Media" wide><input type="text" value={form.media} onInput={set('media')} class={inputClass} /></Input>
            </div>

            <p class="mt-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Standard density (CMYK)</p>
            <div class="grid grid-cols-4 gap-2">
              {['C','M','Y','K'].map((ch) => (
                <Input key={ch} label={ch}>
                  <input type="number" step="0.01" value={form['density'+ch]} onInput={set('density'+ch)} class={inputClass} />
                </Input>
              ))}
            </div>

            <p class="mt-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">With-white density (CMYK)</p>
            <div class="grid grid-cols-4 gap-2">
              {['C','M','Y','K'].map((ch) => (
                <Input key={ch} label={ch}>
                  <input type="number" step="0.01" value={form['densityWt'+ch]} onInput={set('densityWt'+ch)} class={inputClass} />
                </Input>
              ))}
            </div>

            <Input label="Notes" wide>
              <textarea rows={3} value={form.notes} onInput={set('notes')} class={`${inputClass} resize-none`} />
            </Input>

            {error && (
              <p class="text-[13px] font-semibold text-ink-magenta">
                {error instanceof ApiError ? error.message : (error.message ?? 'Failed')}
              </p>
            )}
          </div>
        )}

        <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          {mode === 'edit' && editing && isAdmin && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
          <div class="ml-auto flex gap-2">
            {mode === 'view' && isAdmin && editing && <Button variant="secondary" onClick={() => setMode('edit')}>Edit</Button>}
            {mode === 'edit' && <Button variant="secondary" onClick={() => editing ? setMode('view') : onClose()} disabled={busy}>Cancel</Button>}
            {mode === 'view' && <Button variant="secondary" onClick={onClose}>Close</Button>}
            {mode === 'edit' && (
              <Button variant="primary" onClick={save} disabled={busy || !form.profileName?.trim() || !form.customerId}>
                {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PRINT_TYPE_BUCKETS = ['SP', 'RP', 'CBW SP'];

const ReadingsSection = ({ profileId, customerId, isAdmin, onClose }) => {
  const { route } = useLocation();
  const { data: readings, loading } = useApi(profileId ? `/customer-profiles/${profileId}/readings` : null);
  const [activeBucket, setActiveBucket] = useState('');  // '' = all
  useEffect(() => { setActiveBucket(''); }, [profileId]);

  const counts = useMemo(() => {
    const c = { '': readings?.length ?? 0, SP: 0, RP: 0, 'CBW SP': 0, '__other': 0 };
    (readings ?? []).forEach((r) => {
      const k = r.printType ?? '';
      if (PRINT_TYPE_BUCKETS.includes(k)) c[k] += 1;
      else c['__other'] += 1;
    });
    return c;
  }, [readings]);

  if (!profileId) return null;
  const openReading = (rid) => { onClose?.(); route(`/ink-density/${rid}`); };
  const newReading = () => { onClose?.(); route(`/ink-density/new?customer=${customerId}&profile=${profileId}`); };

  const filtered = (readings ?? []).filter((r) => {
    if (!activeBucket) return true;
    if (activeBucket === '__other') return !PRINT_TYPE_BUCKETS.includes(r.printType ?? '');
    return (r.printType ?? '') === activeBucket;
  });

  const bucketBtn = (val, label, count) => (
    <button
      key={val || 'all'}
      type="button"
      onClick={() => setActiveBucket(val)}
      class={`rounded-full border px-3 py-1 text-[11px] font-mono ${
        activeBucket === val
          ? 'border-brand bg-brand/12 text-brand'
          : 'border-border-strong bg-bg-card text-text-tertiary hover:text-text-primary'
      }`}
    >{label} <span class="text-text-muted">{count}</span></button>
  );

  return (
    <div>
      <div class="mb-2 flex items-center justify-between">
        <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">
          Density readings {readings ? `· ${readings.length}` : ''}
        </p>
        {isAdmin && (
          <button onClick={newReading} class="text-[11px] text-brand">+ New reading</button>
        )}
      </div>

      {readings && readings.length > 0 && (
        <div class="mb-2 flex flex-wrap gap-1.5">
          {bucketBtn('', 'All', counts[''])}
          {bucketBtn('SP', 'SP', counts['SP'])}
          {bucketBtn('RP', 'RP', counts['RP'])}
          {bucketBtn('CBW SP', 'CBW SP', counts['CBW SP'])}
          {counts['__other'] > 0 && bucketBtn('__other', 'Other', counts['__other'])}
        </div>
      )}

      {loading && <p class="text-[12px] text-text-muted">Loading…</p>}
      {readings && readings.length === 0 && !loading && (
        <p class="text-[12px] text-text-muted">No readings linked to this profile yet.</p>
      )}
      {readings && readings.length > 0 && filtered.length === 0 && (
        <p class="text-[12px] text-text-muted">No readings in this folder.</p>
      )}
      {filtered.length > 0 && (
        <div class="overflow-hidden rounded-md border border-border-subtle">
          {filtered.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => openReading(r.id)}
              class="flex w-full items-baseline justify-between gap-3 border-b border-border-subtle px-3 py-2 text-left last:border-b-0 hover:bg-bg-hover"
            >
              <div class="min-w-0">
                <span class="font-mono text-[12px] font-semibold text-text-primary">{r.jobNumber || `#${r.id}`}</span>
                {r.jobName && <span class="ml-2 text-[11px] text-text-secondary">{r.jobName}</span>}
              </div>
              <div class="flex items-center gap-2">
                {r.printType && <span class="rounded-full bg-bg-base px-1.5 font-mono text-[10px] text-text-tertiary">{r.printType}</span>}
                <span class="font-mono text-[11px] text-text-tertiary">{r.readingDate || ''}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const inputClass = 'block w-full rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const Input = ({ label, wide = false, children }) => (
  <div class={wide ? 'col-span-2' : ''}>
    <p class="mb-1 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">{label}</p>
    {children}
  </div>
);

const Field = ({ label, value, mono = false, muted = false }) => (
  <div>
    <p class="mb-0.5 text-[10px] font-semibold uppercase tracking-eyebrow text-text-muted">{label}</p>
    <p class={`m-0 ${mono ? 'font-mono text-[12px]' : 'text-[13px]'} ${muted ? 'text-text-tertiary' : 'text-text-body'}`}>
      {value == null || value === '' ? '—' : value}
    </p>
  </div>
);

const selectClass = 'rounded-md border border-border-strong bg-bg-card px-2 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

export const CustomerProfilesPage = () => {
  const initialCustomer = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('customer')) || '';
  const [search, setSearch] = useState('');
  const [pressType, setPressType] = useState('');
  const [printSide, setPrintSide] = useState('');
  const [status, setStatus] = useState('');
  const [customerFilter, setCustomerFilter] = useState(initialCustomer);
  const [open, setOpen] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const params = useMemo(() => {
    const q = new URLSearchParams();
    if (search) q.set('search', search);
    if (pressType) q.set('pressType', pressType);
    if (printSide) q.set('printSide', printSide);
    if (status) q.set('status', status);
    if (customerFilter) q.set('customer', customerFilter);
    const s = q.toString();
    return s ? `?${s}` : '';
  }, [search, pressType, printSide, status, customerFilter]);

  const { data: profiles, loading, error, refetch } = useApi(`/customer-profiles${params}`);
  const { data: filters } = useApi('/customer-profiles/filters');

  const customerFilterName = useMemo(
    () => filters?.customers?.find((c) => String(c.id) === String(customerFilter))?.name,
    [customerFilter, filters],
  );

  return (
    <div>
      <section class="mb-5 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: ORANGE }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: ORANGE }}>Customer profiles</span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Press profile database</h1>
          <p class="m-0 text-sm text-text-secondary">
            Search and manage customer-specific press setups, plate curves, and density targets.
          </p>
        </div>
        {isAdmin && <Button variant="primary" onClick={() => setNewOpen(true)}>+ New profile</Button>}
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search profiles or customers…"
          class="block w-72 max-w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <select value={pressType} onChange={(e) => setPressType(e.currentTarget.value)} class={selectClass}>
          <option value="">All press types</option>
          {filters?.pressTypes?.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={printSide} onChange={(e) => setPrintSide(e.currentTarget.value)} class={selectClass}>
          <option value="">All sides</option>
          {PRINT_SIDE_FILTERS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.currentTarget.value)} class={selectClass}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {customerFilter && (
          <span class="inline-flex items-center gap-1.5 rounded-full border border-brand bg-brand/12 px-3 py-1 text-[11px] font-semibold text-brand">
            Customer: {customerFilterName ?? `#${customerFilter}`}
            <button type="button" onClick={() => setCustomerFilter('')} class="text-brand hover:text-text-primary" aria-label="Clear customer filter">×</button>
          </span>
        )}
        <span class="ml-auto text-[12px] text-text-tertiary">
          {profiles ? `${profiles.length} profile${profiles.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && (
        <div class="rounded-md border border-border-strong bg-bg-card p-3 text-sm text-text-secondary">
          Couldn’t load — {error.message || 'request failed'}{error.status ? ` (${error.status})` : ''}.
        </div>
      )}
      {profiles && profiles.length === 0 && !loading && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No matching profiles.</p>
          <p class="m-0 mt-1 text-sm text-text-secondary">Adjust the search or filters above.</p>
        </div>
      )}
      {profiles && profiles.length > 0 && (
        <div class="space-y-1.5">
          {profiles.map((p) => <ProfileCard key={p.id} profile={p} onOpen={setOpen} />)}
        </div>
      )}

      {open && (
        <DetailModal
          profile={open}
          customers={filters?.customers}
          isAdmin={isAdmin}
          onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); refetch(); }}
        />
      )}
      {newOpen && (
        <DetailModal
          profile={{ customerId: filters?.customers?.[0]?.id, profileName: '', status: 'active', tags: [], density: {}, densityWt: {} }}
          customers={filters?.customers}
          isAdmin={isAdmin}
          onClose={() => setNewOpen(false)}
          onSaved={() => { setNewOpen(false); refetch(); }}
        />
      )}
    </div>
  );
};
